import { Alert, AlertIcon, Box, useToast } from "@chakra-ui/react";
import { isFrameworkAward } from "./deriveNowPanel";
import { CreateFrameworkAgreementModal, callOffMechanismFor } from "../award/CreateFrameworkAgreementModal";
import { urls } from "../../../urls";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useBeginEvaluationMutation,
  useCompleteNegotiationRoundMutation,
  useDispatchAwardLettersMutation,
  useCompleteSourcingEventMutation,
  useConfirmAwardMutation,
  useConfirmJuryReportMutation,
  useReopenCompletedSourcingEventMutation,
  useDeclineResultNoticeMutation,
  useSubmitIntentionNoticeMutation,
  useStartDesignContestFollowUpMutation,
  EFormType,
  type FieldErrorDto,
  type SourcingEventDto,
  bffApi,
} from "../../../autogen/bff-api";
import { RejectedRankedEntriesNotice } from "../designContest/RejectedRankedEntriesNotice";
import { useApiError } from "../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { formatTimeLeft } from "../../../common/formatting/timeLeft";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { isFlaggedEntriesRefusal } from "../designContest/flaggedEntriesRefusal";
import { useAppDispatch } from "../../../common/redux/hooks";
import { useCompleteEvaluationDescription } from "../evaluation/useCompleteEvaluationDescription";
import { bidDocuments } from "../award/bidDocuments";
import { CreateContractFromBidModal } from "../award/CreateContractFromBidModal";
import { buildDoffinNoticeUrl } from "../doffinNoticeUrl";
import { AnnounceResultModal } from "../result-notice/AnnounceResultModal";
import { DoffinNoAnswerAlert } from "../result-notice/DoffinNoAnswerAlert";
import { DoffinRefusalAlert, doffinRefusal, isResultNoticeAlreadySent } from "../result-notice/DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "../result-notice/doffinNoAnswer";
import {
  awardDecisionDay,
  standstillEnd,
} from "../result-notice/concludedDate";
import { ContractConcludedModal } from "../result-notice/ContractConcludedModal";
import { CancellationNoticeModal } from "../result-notice/CancellationNoticeModal";
import { NoWinnerNoticeModal } from "../result-notice/NoWinnerNoticeModal";
import { displayDate } from "../../../common/formatting/displayDate";
import { useStartRound } from "../rounds/useStartRound";
import { requiresStandstill } from "../award/requiresStandstill";
import { StaleDecisionsBanner } from "../evaluation/staleScore";
import { scrollToEventTabs } from "../eventTabsAnchor";
import type { EventTab } from "../EventTabs";
import { useGoToEventTab } from "../useGoToEventTab";
import { useCancelOrCloseProcurement } from "../useCancelOrCloseProcurement";
import { useChangeFlow } from "../changes/useChangeFlow";
import {
  deriveNowPanel,
  type NowAction,
  type NowActionTarget,
  type NowCommandTarget,
} from "./deriveNowPanel";
import { NowPanel, type NowPanelAction } from "./NowPanel";
import { AwardConfirmationModal } from "./AwardConfirmationModal";
import { joinDetail } from "./joinDetail";
import { useNowPanelData } from "./useNowPanelData";
import { flaggedAmong } from "../designContest/jury";
import { eventIsDesignContest, isDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";

export const EventNowPanel = ({
  event,
  activeTab,
}: {
  event: SourcingEventDto;
  activeTab: EventTab;
}) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const goToTab = useGoToEventTab();
  const completeEvaluationDescription = useCompleteEvaluationDescription(event);
  const [beginEvaluation, { isLoading: isBeginning }] =
    useBeginEvaluationMutation();
  const [startFollowUp, { isLoading: isStartingFollowUp }] = useStartDesignContestFollowUpMutation();
  const [completeEvaluation, { isLoading: isCompleting }] =
    useCompleteSourcingEventMutation();
  const [reopenEvaluation, { isLoading: isReopening }] =
    useReopenCompletedSourcingEventMutation();
  const [confirmAward, { isLoading: isConfirming }] = useConfirmAwardMutation();
  const [confirmJuryReport, { isLoading: isConfirmingJuryReport }] = useConfirmJuryReportMutation();
  const [completeRound, { isLoading: isCompletingRound }] =
    useCompleteNegotiationRoundMutation();
  const [sendAwardLetters, { isLoading: isSendingLetters }] =
    useDispatchAwardLettersMutation();
  const [declineResultNotice, { isLoading: isDecliningResultNotice }] =
    useDeclineResultNoticeMutation();
  const [submitIntentionNotice, { isLoading: isSendingIntentionNotice }] =
    useSubmitIntentionNoticeMutation();
  const { award, isAwarding, refusedOverFlags, clearRefusedOverFlags } = useAwardSelection();
  const [confirmsAward, setConfirmsAward] = useState(false);
  const dispatch = useAppDispatch();
  const [reopenAfterRefresh, setReopenAfterRefresh] = useState<NowCommandTarget | null>(null);
  const cancelOrClose = useCancelOrCloseProcurement(event);
  const change = useChangeFlow(event);
  const startRound = useStartRound(event);
  const [contractBidId, setContractBidId] = useState<string | null>(null);
  const [createsFrameworkAgreement, setCreatesFrameworkAgreement] = useState(false);
  const [resultDialog, setResultDialog] = useState<
    "concludedDate" | "announce" | "noWinner" | "cancellationNotice" | null
  >(null);
  const [intentionNoticeRefusal, setIntentionNoticeRefusal] = useState<FieldErrorDto[] | null>(null);
  const [intentionNoticeNoAnswer, setIntentionNoticeNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const data = useNowPanelData(event, activeTab);
  const contest = event.eventContext.type === "Public" ? event.eventContext.designContest : null;
  const flaggedRanked = flaggedAmong(contest, (contest?.juryReport?.rankedEntries ?? []).map((ranked) => ranked.bidId));
  const entryNameOf = (bidId: string) => data.bids.find((bid) => bid.id === bidId)?.ownerName ?? String(t("Entry"));
  const state = deriveNowPanel(data);
  const contractBid = data.bids.find((bid) => bid.id === contractBidId);

  const pending: Record<NowCommandTarget["command"], boolean> = {
    beginEvaluation: isBeginning,
    completeQualification: isBeginning,
    completeEvaluation: isCompleting,
    reopenEvaluation: isReopening,
    awardEvent: isAwarding,
    confirmAward: isConfirming,
    completeRound: isCompletingRound,
    confirmJuryReport: isConfirmingJuryReport,
    createContract: false,
    createFrameworkAgreement: false,
    sendAwardLetters: isSendingLetters,
    cancelProcurement: false,
    extendDeadline: change.isStarting,
    continueChange: false,
    recordContractConcluded: false,
    announceResult: false,
    announceNoWinner: false,
    announceCancellation: false,
    declineResultNotice: isDecliningResultNotice,
    sendIntentionNotice: isSendingIntentionNotice,
    startDesignContestFollowUp: isStartingFollowUp,
    concludeWithoutIntentionNotice: false,
    startDialogue: startRound.isStarting,
    retryEvaluation: false,
  };

  const run = async (target: NowCommandTarget) => {
    try {
      switch (target.command) {
        case "createContract":
          setContractBidId(target.bidId);
          return;
        case "createFrameworkAgreement":
          setCreatesFrameworkAgreement(true);
          return;
        case "startDesignContestFollowUp": {
          const followUp = await startFollowUp({ eventId: event.id }).unwrap();
          navigate(urls.app.sourcingEvents.edit.go(followUp.id).info);
          return;
        }
        case "recordContractConcluded":
        case "concludeWithoutIntentionNotice":
          setResultDialog("concludedDate");
          return;
        case "sendIntentionNotice":
          setIntentionNoticeRefusal(null);
          setIntentionNoticeNoAnswer(null);
          await submitIntentionNotice({ sourcingEventId: event.id }).unwrap();
          toast({
            title: t("The intention notice is sent to Doffin"),
            status: "success",
            position: "top-right",
          });
          return;
        case "announceNoWinner":
          setResultDialog("noWinner");
          return;
        case "announceCancellation":
          setResultDialog("cancellationNotice");
          return;
        case "announceResult":
          setResultDialog("announce");
          return;
        case "declineResultNotice":
          await declineResultNotice({ eventId: event.id }).unwrap();
          return;
        case "cancelProcurement":
          cancelOrClose.open("cancel");
          return;
        case "extendDeadline":
          await change.startChangeAtDeadline();
          return;
        case "continueChange":
          change.openChangeAtDeadline();
          return;
        case "startDialogue":
          startRound.start();
          return;
        case "beginEvaluation":
        case "completeQualification":
          await beginEvaluation({ eventId: event.id }).unwrap();
          return;
        case "completeEvaluation":
          await completeEvaluation({
            eventId: event.id,
            ...(flaggedRanked.length > 0 ? { proceedDespiteFlaggedEntries: true } : {}),
          }).unwrap();
          return;
        case "reopenEvaluation":
          await reopenEvaluation({ eventId: event.id }).unwrap();
          return;
        case "awardEvent":
          await award();
          return;
        case "retryEvaluation":
          data.retryEvaluation();
          return;
        case "confirmJuryReport":
          await confirmJuryReport({
            eventId: event.id,
            confirmJuryReportRequest: { memberIndex: target.memberIndex, reportWrittenAt: target.reportWrittenAt },
          }).unwrap();
          return;
        case "completeRound":
          await completeRound({
            eventId: event.id,
            roundId: target.roundId,
          }).unwrap();
          toast({
            title: t("Negotiation round completed"),
            status: "success",
            position: "top-right",
          });
          return;
        case "sendAwardLetters":
          await sendAwardLetters({ eventId: event.id }).unwrap();
          toast({
            title: t("The award letters are sent"),
            status: "success",
            position: "top-right",
          });
          return;
        case "confirmAward":
          await confirmAward({ eventId: event.id }).unwrap();
          toast({
            title: t("Award confirmed"),
            status: "success",
            position: "top-right",
          });
      }
    } catch (e) {
      const refusal = target.command === "sendIntentionNotice" ? doffinRefusal(e) : null;
      const unanswered = target.command === "sendIntentionNotice" ? doffinNoAnswer(e) : null;
      if (refusal) setIntentionNoticeRefusal(refusal);
      else if (unanswered) setIntentionNoticeNoAnswer(unanswered);
      else if (target.command === "completeEvaluation" && isFlaggedEntriesRefusal(e)) {
        setReopenAfterRefresh(target);
        dispatch(bffApi.util.invalidateTags(["SourcingEvent"]));
      } else if (target.command === "beginEvaluation") trigger(e);
      else if (target.command === "declineResultNotice" && isResultNoticeAlreadySent(e)) trigger(e);
      else trigger(e);
    }
  };

  const confirmationFor = (
    command: NowCommandTarget["command"],
  ): { title: string; description: ReactNode } | null => {
    switch (command) {
      case "completeQualification":
        return {
          title: t("Complete qualification"),
          description: t(
            "The qualification is locked and the evaluation of the tenders opens. A rejection can still be undone until the award decision.",
          ),
        };
      case "completeEvaluation":
        return {
          title: isDesignContest(event.eventContext.type === "Public" ? event.eventContext.procedureType : null)
            ? t("Register the jury's decision?")
            : t("Complete the evaluation?"),
          description: (
            <>
              {completeEvaluationDescription()}
              <RejectedRankedEntriesNotice event={event} />
              {flaggedRanked.length > 0 && (
                <Alert status="warning" rounded="md" mt="3" fontSize="sm">
                  <AlertIcon />
                  {t(
                    "The jury has flagged {{entries}} as identifying. Registering the decision records in the protocol that you went ahead.",
                    { entries: flaggedRanked.map(entryNameOf).join(", "), count: flaggedRanked.length },
                  )}
                </Alert>
              )}
              <Box mt="3">
                <StaleDecisionsBanner bids={data.evaluation?.bids ?? []} />
              </Box>
            </>
          ),
        };
      case "reopenEvaluation":
        return {
          title: t("Reopen the evaluation?"),
          description: t("The procurement is moved back to evaluation."),
        };
      case "completeRound":
        return {
          title: t("Complete negotiation round?"),
          description: t(
            "Suppliers will no longer be able to submit bids for this round.",
          ),
        };
      case "sendAwardLetters":
        if (eventIsDesignContest(event)) {
          return {
            title: t("Send the letters?"),
            description: t("Every participant gets their letter. This cannot be undone."),
          };
        }
        return {
          title: t("Send the award letters?"),
          description:
            requiresStandstill(event) && !event.award?.standstillExemption
              ? t(
                  "Every supplier gets their letter and the standstill period starts. This cannot be undone.",
                )
              : t("Every supplier gets their letter. This cannot be undone."),
        };
      case "sendIntentionNotice":
        return {
          title: t("Send the intention notice?"),
          description: t(
            "Doffin and TED publish the supplier you chose and the ground for awarding the contract without a notice. The contract can be concluded once ten days have passed, counted from the day after the notice is published.",
          ),
        };
      case "concludeWithoutIntentionNotice":
        return {
          title: t("Conclude the contract without an intention notice?"),
          description: t(
            "Without an intention notice the contract is not protected against being declared ineffective (LOA § 13). Continue?",
          ),
        };
      case "declineResultNotice":
        return {
          title: t("Do not announce the result?"),
          description: t(
            "The result notice is voluntary for this procurement. Once you choose not to announce it, the reminder goes away for good.",
          ),
        };
      case "confirmAward":
        return {
          title: t("Confirm the award?"),
          description: isFrameworkAward(event)
            ? t("The award is final and the procurement moves on to the framework agreement.")
            : t("The award is final and the procurement moves on to the contract."),
        };
      default:
        return null;
    }
  };

  const go = (target: NowActionTarget) => {
    if (target.kind === "tab") {
      goToTab(
        target.tab,
        target.subTab,
        target.focusBidId,
        target.reveal,
        target.round,
      );
      if (target.reveal == null) scrollToEventTabs();
      return;
    }
    if (target.kind === "url") {
      navigate(target.url);
      return;
    }
    if (target.kind === "doffinNotice") {
      window.open(
        buildDoffinNoticeUrl(target.externalId),
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    if (target.command === "awardEvent") {
      setConfirmsAward(true);
      return;
    }
    if (target.command === "confirmJuryReport") {
      const contest = event.eventContext.type === "Public" ? event.eventContext.designContest : null;
      confirmationModal.show({
        title: t("Confirm the jury's report as {{name}}?", { name: contest?.jury?.[target.memberIndex]?.name ?? "" }),
        description: t("A confirmation can't be withdrawn. Changing the report later asks every member to confirm again."),
        onConfirmed: () => run(target),
      });
      return;
    }
    const confirmation = confirmationFor(target.command);
    if (confirmation == null) {
      void run(target);
      return;
    }
    confirmationModal.show({ ...confirmation, onConfirmed: () => run(target) });
  };

  useEffect(() => {
    if (reopenAfterRefresh) {
      setReopenAfterRefresh(null);
      go(reopenAfterRefresh);
    }
    if (refusedOverFlags) {
      clearRefusedOverFlags();
      setConfirmsAward(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const toPanelAction = (candidate: NowAction): NowPanelAction => ({
    label: t(candidate.labelKey, candidate.labelParams ?? {}),
    icon: candidate.icon,
    tone: candidate.tone,
    onClick: () => go(candidate.target),
    isLoading:
      candidate.target.kind === "command" && pending[candidate.target.command],
    isDisabled:
      candidate.disabled === true ||
      (candidate.target.kind === "command" &&
      candidate.target.command === "sendIntentionNotice" &&
      intentionNoticeNoAnswer === "whileSending"),
  });

  if (state == null) return null;
  const { action, secondary, alternatives = [] } = state;

  return (
    <>
      {confirmationModal.modal}
      {confirmsAward && (
        <AwardConfirmationModal
          event={event}
          bids={data.bids}
          evaluationBids={data.evaluation?.bids ?? []}
          onConfirm={() => run({ kind: "command", command: "awardEvent" })}
          onClose={() => setConfirmsAward(false)}
        />
      )}
      {cancelOrClose.modal}
      {startRound.modal}
      <NowPanel
        isLoading={state.key === "LOADING"}
        status={t(state.status.key, {
          ...state.status.params,
          ...(state.status.dateIso
            ? { date: displayDateWithTime(state.status.dateIso) }
            : {}),
          ...(state.status.dayIso
            ? { date: displayDate(state.status.dayIso) }
            : {}),
          ...(state.status.timeLeft
            ? { timeLeft: formatTimeLeft(state.status.timeLeft, t) }
            : {}),
        })}
        detail={
          joinDetail(
            state.detail.map((segment) =>
              [t(segment.key, segment.params ?? {}), segment.text]
                .filter(Boolean)
                .join(": "),
            ),
          ) || undefined
        }
        progress={state.progress}
        action={action && toPanelAction(action)}
        secondaryAction={secondary && toPanelAction(secondary)}
        alternatives={alternatives.map(toPanelAction)}
      />
      {intentionNoticeRefusal && (
        <Box mt="3">
          <DoffinRefusalAlert
            breaches={intentionNoticeRefusal}
            heading={t("Doffin refused the intention notice. Nothing has been announced.")}
          />
        </Box>
      )}
      {intentionNoticeNoAnswer && (
        <Box mt="3">
          <DoffinNoAnswerAlert noAnswer={intentionNoticeNoAnswer} />
        </Box>
      )}
      {resultDialog === "concludedDate" && (
        <ContractConcludedModal
          eventId={event.id}
          concludedOn={event.award?.contractConcludedOn}
          awardDecisionDay={awardDecisionDay(event.award)}
          standstill={standstillEnd(event)}
          contractFrom={event.award?.intentionNotice?.contractFrom}
          injunctionSuspension={event.award?.injunctionSuspension}
          isOpen
          onClose={() => setResultDialog(null)}
        />
      )}
      {resultDialog === "announce" && (
        <AnnounceResultModal
          eventId={event.id}
          concludedOn={event.award?.contractConcludedOn}
          awardDecisionDay={awardDecisionDay(event.award)}
          standstill={standstillEnd(event)}
          contractFrom={event.award?.intentionNotice?.contractFrom}
          injunctionSuspension={event.award?.injunctionSuspension}
          isOpen
          onClose={() => setResultDialog(null)}
        />
      )}
      {resultDialog === "noWinner" && (
        <NoWinnerNoticeModal
          eventId={event.id}
          reason={event.cancellation?.nonAwardReason ?? undefined}
          relaunch={event.cancellation?.relaunch ?? undefined}
          isOpen
          onClose={() => setResultDialog(null)}
        />
      )}
      {resultDialog === "cancellationNotice" && (
        <CancellationNoticeModal
          eventId={event.id}
          reason={event.cancellation?.reason ?? ""}
          isOpen
          onClose={() => setResultDialog(null)}
        />
      )}
      {createsFrameworkAgreement && (
        <CreateFrameworkAgreementModal
          eventId={event.id}
          defaultTitle={event.eventData.title}
          defaultMechanism={callOffMechanismFor(
            event.eventContext.type === "Public" ? event.eventContext.frameworkAgreement : undefined,
          )}
          supplierCount={
            new Set(
              data.bids.filter((bid) => event.award?.winningBidIds?.includes(bid.id)).map((bid) => bid.owner),
            ).size
          }
          isOpen
          onClose={() => setCreatesFrameworkAgreement(false)}
        />
      )}
      {contractBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractBid.id}
          supplierName={contractBid.ownerName}
          defaultTitle={event.eventData.title}
          procurementDocuments={event.eventData.documents ?? []}
          bidDocuments={bidDocuments(contractBid)}
          isOpen={contractBidId !== null}
          onClose={() => setContractBidId(null)}
        />
      )}
    </>
  );
};
